import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import api from "@/utils/axios";
import i18n from "@/i18n";
import { toast } from "react-toastify";
import PortalIconMini from "@/components/icons/PortalIconMini";
import cls from "./LoginInfo.module.scss";

const fadeInUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

const LoginInfo = () => {
  // from HomeLayout outlet, if you want a small “Hi”
  const outletCtx = useOutletContext() || {};
  const { rawUsername: ctxUsername } = outletCtx;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);

  const [initialUsername, setInitialUsername] = useState("");
  const [initialEmail, setInitialEmail] = useState("");

  const [form, setForm] = useState({
    username: "",
    email: "",
    newEmail: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [pendingEmail, setPendingEmail] = useState(null);

  useEffect(() => {
    let ignore = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setServerError(null);
        const { data } = await api.get("/v1/member/profile/login-info");
        if (ignore) return;

        const u = data?.user || {};
        setInitialUsername(u.username || "");
        setInitialEmail(u.email || "");

        setForm((prev) => ({
          ...prev,
          username: u.username || "",
          email: u.email || "",
        }));

        setPendingEmail(u.pendingEmail || null);
      } catch (err) {
        if (!ignore) {
          setServerError(
            err?.response?.data?.message ||
              i18n.t("memberLoginInfo.errors.loadFailed"),
          );
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchData();
    return () => {
      ignore = true;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError(null);

    // simple client-side password match check
    if (
      (form.newPassword || form.confirmPassword) &&
      form.newPassword !== form.confirmPassword
    ) {
      toast.error(i18n.t("memberLoginInfo.errors.passwordMismatch"));
      return;
    }

    const payload = {};

    if (form.username && form.username !== initialUsername) {
      payload.username = form.username.trim();
    }

    const trimmedNewEmail = form.newEmail.trim().toLowerCase();
    if (trimmedNewEmail && trimmedNewEmail !== initialEmail.toLowerCase()) {
      payload.newEmail = trimmedNewEmail;
    }

    if (form.currentPassword || form.newPassword || form.confirmPassword) {
      payload.currentPassword = form.currentPassword;
      payload.newPassword = form.newPassword;
      payload.confirmPassword = form.confirmPassword;
    }

    // if nothing changed, just exit
    if (!Object.keys(payload).length) {
      toast.info(i18n.t("memberLoginInfo.messages.nothingToUpdate"));
      return;
    }

    payload.lang = i18n.language;

    try {
      setSaving(true);
      const { data } = await api.patch(
        "/v1/member/profile/login-info",
        payload,
      );

      const u = data?.user || {};
      setInitialUsername(u.username || "");
      setInitialEmail(u.email || "");

      setForm((prev) => ({
        ...prev,
        username: u.username || "",
        email: u.email || "",
        // keep newEmail so user can see what they entered, or clear:
        newEmail: "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));

      setPendingEmail(u.pendingEmail || null);

      if (data?.emailVerificationSent) {
        toast.success(i18n.t("memberLoginInfo.messages.emailVerifySent"));
      } else if (data?.passwordChanged) {
        toast.success(i18n.t("memberLoginInfo.messages.passwordUpdated"));
      } else {
        toast.success(i18n.t("memberLoginInfo.messages.profileUpdated"));
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        i18n.t("memberLoginInfo.errors.saveFailed");
      setServerError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={cls.page}>
      <div className={cls.shell}>
        {/* LEFT / HERO SIDE */}
        <Motion.div
          className={cls.hero}
          variants={fadeInUp}
          initial="hidden"
          animate="show"
        >
          <div className={cls.heroBadge}>
            <PortalIconMini size={22} title="Noty Portal" data-preserve />
            <span>{i18n.t("memberLoginInfo.badge")}</span>
          </div>

          <h1 className={cls.title}>{i18n.t("memberLoginInfo.title")}</h1>
          <p className={cls.subtitle}>
            {i18n.t("memberLoginInfo.subtitle", {
              username: form.username || ctxUsername || "",
            })}
          </p>

          <ul className={cls.pillList}>
            <li>{i18n.t("memberLoginInfo.pills.usernameSafe")}</li>
            <li>{i18n.t("memberLoginInfo.pills.passwordProtected")}</li>
            <li>{i18n.t("memberLoginInfo.pills.emailVerified")}</li>
          </ul>

          <div className={cls.glowOrb} aria-hidden="true" />
        </Motion.div>

        {/* RIGHT / FORM SIDE */}
        <Motion.div
          className={cls.panel}
          variants={fadeInUp}
          initial="hidden"
          animate="show"
        >
          {loading ? (
            <div className={cls.skeleton}>
              <div className={cls.skelLine} />
              <div className={cls.skelLine} />
              <div className={cls.skelLineWide} />
            </div>
          ) : (
            <>
              {serverError && (
                <div className={cls.errorBox}>
                  <span>{serverError}</span>
                </div>
              )}

              {pendingEmail && (
                <div className={cls.infoBox}>
                  <strong>
                    {i18n.t("memberLoginInfo.pendingEmail.title")}
                  </strong>
                  <p>
                    {i18n.t("memberLoginInfo.pendingEmail.text", {
                      email: pendingEmail,
                    })}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className={cls.form}>
                {/* USERNAME / EMAIL */}
                <div className={cls.section}>
                  <div className={cls.sectionHeader}>
                    <h2 className={cls.sectionTitle}>
                      {i18n.t("memberLoginInfo.sections.identity.title")}
                    </h2>
                    <p className={cls.sectionHelp}>
                      {i18n.t("memberLoginInfo.sections.identity.help")}
                    </p>
                  </div>

                  <div className={cls.fieldGrid}>
                    <div className={cls.field}>
                      <label htmlFor="username" className={cls.label}>
                        {i18n.t("memberLoginInfo.labels.username")}
                      </label>
                      <input
                        id="username"
                        name="username"
                        type="text"
                        autoComplete="username"
                        className={cls.input}
                        value={form.username}
                        onChange={handleChange}
                      />
                      <p className={cls.helpText}>
                        {i18n.t("memberLoginInfo.help.username")}
                      </p>
                    </div>

                    <div className={cls.field}>
                      <label htmlFor="email" className={cls.label}>
                        {i18n.t("memberLoginInfo.labels.currentEmail")}
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        className={`${cls.input} ${cls.inputReadonly}`}
                        value={form.email}
                        readOnly
                      />
                      <p className={cls.helpText}>
                        {i18n.t("memberLoginInfo.help.currentEmail")}
                      </p>
                    </div>

                    <div className={cls.field}>
                      <label htmlFor="newEmail" className={cls.label}>
                        {i18n.t("memberLoginInfo.labels.newEmail")}
                      </label>
                      <input
                        id="newEmail"
                        name="newEmail"
                        type="email"
                        autoComplete="email"
                        className={cls.input}
                        value={form.newEmail}
                        onChange={handleChange}
                        placeholder={i18n.t(
                          "memberLoginInfo.placeholders.newEmail",
                        )}
                      />
                      <p className={cls.helpText}>
                        {i18n.t("memberLoginInfo.help.newEmail")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* PASSWORD */}
                <div className={cls.section}>
                  <div className={cls.sectionHeader}>
                    <h2 className={cls.sectionTitle}>
                      {i18n.t("memberLoginInfo.sections.password.title")}
                    </h2>
                    <p className={cls.sectionHelp}>
                      {i18n.t("memberLoginInfo.sections.password.help")}
                    </p>
                  </div>

                  <div className={cls.fieldGrid}>
                    <div className={cls.field}>
                      <label htmlFor="currentPassword" className={cls.label}>
                        {i18n.t("memberLoginInfo.labels.currentPassword")}
                      </label>
                      <input
                        id="currentPassword"
                        name="currentPassword"
                        type="password"
                        autoComplete="current-password"
                        className={cls.input}
                        value={form.currentPassword}
                        onChange={handleChange}
                      />
                    </div>

                    <div className={cls.field}>
                      <label htmlFor="newPassword" className={cls.label}>
                        {i18n.t("memberLoginInfo.labels.newPassword")}
                      </label>
                      <input
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        autoComplete="new-password"
                        className={cls.input}
                        value={form.newPassword}
                        onChange={handleChange}
                      />
                    </div>

                    <div className={cls.field}>
                      <label htmlFor="confirmPassword" className={cls.label}>
                        {i18n.t("memberLoginInfo.labels.confirmPassword")}
                      </label>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        className={cls.input}
                        value={form.confirmPassword}
                        onChange={handleChange}
                      />
                      <p className={cls.helpText}>
                        {i18n.t("memberLoginInfo.help.password")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* ACTIONS */}
                <div className={cls.actions}>
                  <button
                    type="submit"
                    className={cls.saveBtn}
                    disabled={saving}
                  >
                    {saving
                      ? i18n.t("memberLoginInfo.actions.saving")
                      : i18n.t("memberLoginInfo.actions.save")}
                  </button>
                  <p className={cls.smallNote}>
                    {i18n.t("memberLoginInfo.footerNote")}
                  </p>
                </div>
              </form>
            </>
          )}
        </Motion.div>
      </div>
    </section>
  );
};

export default LoginInfo;
